import { getMsalInstance } from 'authConfig';
import axios, { AxiosResponse } from 'axios';
import { InvoiceSettings } from './models/Common/Invoice.type';
import { FixedRoute, FixedRoutes } from './supplier/suppliers.types';
import { clean } from './utility/jsonFunctions';
import { getUserToken } from './utility/localStorageFunctions';

const BASE_URL = `${process.env.REACT_APP_BASE_API_URL}/clients-api/clients`

async function getUserProfiles(clientId: number) {
  const token = await getUserToken('clients')

  const response = await axios({
    method: 'get',
    url: BASE_URL.concat(`/${clientId}/userProfiles`),
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
  return response
}
async function updateClientUserProfile(
  clientId: number,
  userProfileId: number,
  data: any,
) {
  const token = await getUserToken('clients')
  const response = await axios({
    method: 'patch',
    url: BASE_URL.concat(`/${clientId}/userProfiles/${userProfileId}`),
    headers: {
      Authorization: `Bearer ${token}`,
    },
    data: {
      data: {
        ...data,
      },
    },
  })
  return response
}

async function getClients() {
  try {
    const token = await getUserToken('clients')

    const response = await axios({
      method: 'get',
      url: BASE_URL,
      params: { limit: 10000 },
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
    return response
  } catch (error) {
    return error
  }
}

async function getClientsByPlatformId(platformId: any, limit?: number) {
  const token = await getUserToken('clients')

  const response = await axios({
    method: 'POST',
    url: `${BASE_URL}/search`,
    headers: {
      Authorization: `Bearer ${token}`,
    },
    params: { limit: limit || 25 },
    data: {
      data: {
        platformId: platformId,
      },
    },
  })

  return response
}

async function searchClients() {
  const token = await getUserToken('clients')

  const response = await axios({
    method: 'POST',
    url: `${BASE_URL}/search`,
    headers: {
      Authorization: `Bearer ${token}`,
    },
    data: {
      data: {
        status: ['Onboarding', 'Active', 'Suspended', 'Inactive'],
      },
    },
    params: {
      //TODO: A handler for this needs to be implemented
      limit: 500,
    },
  })
  return response
}

async function getClientsByParentId(parentId: any) {
  const token = await getUserToken('clients')

  const response = await axios({
    method: 'POST',
    url: `${BASE_URL}/search`,
    headers: {
      Authorization: `Bearer ${token}`,
    },
    data: {
      data: {
        parentClientId: parentId,
      },
    },
  })

  return response
}

async function getClient(clientId: number | string | void) {
  const msalInstance = await getMsalInstance()
  const account = msalInstance.getActiveAccount()
  if (!account) {
    throw Error(
      'No active account! Verify a user has been signed in and setActiveAccount has been called.',
    )
  }

  const token = await getUserToken('clients')

  const headers = new Headers()
  const bearer = `Bearer ${token}`

  headers.append('Authorization', bearer)

  const options = {
    method: 'GET',
    headers: headers,
  }

  return fetch(
    BASE_URL.concat(
      `/${clientId ?? account.idTokenClaims?.['extension_Jyrney_companyId']}`,
    ),
    options,
  ).then((response) => response.json())
}

async function getClientById(clientId: any) {
  const token = await getUserToken('clients')
  const response = await axios({
    method: 'get',
    url: BASE_URL.concat(`/${clientId}`),
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })

  return response.data
}

async function getDescendantsByClientId(clientId: any) {
  const token = await getUserToken('clients')
  let descendants: any[] = []
  const { data } = await axios({
    method: 'POST',
    url: `${BASE_URL}/search`,
    headers: {
      Authorization: `Bearer ${token}`,
    },
    data: {
      data: {
        parentClientId: clientId,
      },
    },
  })
  const ownClient = await getClientById(clientId)
  if (ownClient) descendants.push(ownClient)

  if (data.value.length > 0) {
    await data.value.map(async (client: any) => {
      descendants.push(client)
      const descendant = await getDescendantsByClientId(String(client.id))
      if (descendant.length) {
        descendants.push(descendant[0])
      }
    })

    return descendants
  }

  return descendants
}

async function getAllDescendants() {
  const token = await getUserToken('clients')
  let allDescendants: any[] = []
  const { data } = await axios({
    method: 'get',
    url: BASE_URL.concat('?limit=10000'),
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
  if (data.value.length) {
    allDescendants.push(data.value)
    data.value.map(async (client: any) => {
      const descendant = await getDescendantsByClientId(String(client.id))
      if (descendant.length) {
        allDescendants.push(descendant[0])
      }
    })
  }

  return allDescendants
}

async function getAllClientsByCurrentId(id: any) {
  const rs1 = await getClient()
  const rs2 = await getClientsByParentId(id)

  const result = await Promise.all([rs1, rs2]).then((values) => {
    values[1].data.value.push(rs1.data)
    return values[1]
  })

  return result
}

async function createClient(client: any) {
  clean(client)
  const token = await getUserToken('clients')
  const response = await axios({
    method: 'post',
    url: BASE_URL,
    headers: {
      Authorization: `Bearer ${token}`,
    },
    data: {
      data: {
        ...client,
      },
    },
  })
  return response
}

async function updateClient(data: any, clientId: number) {
  clean(data)
  const token = await getUserToken('clients')
  const response = await axios({
    method: 'PATCH',
    url: BASE_URL.concat(`/${clientId}`),
    headers: {
      Authorization: `Bearer ${token}`,
    },
    data: {
      ...data,
    },
  })
  return response
}

const deleteClient = async (clientId: number) => {
  const token = await getUserToken('clients')
  const response = await axios({
    method: 'delete',
    url: BASE_URL?.concat(`/${clientId}`),
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
  return response
}

async function getBookingReferences(clientId: any) {
  const token = await getUserToken('clients')
  const response = await axios({
    method: 'get',
    url: BASE_URL.concat(`/${clientId}/bookingReferences`),
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
  return response
}

async function getUserProperties(clientId: any) {
  const token = await getUserToken('clients')
  const response = await axios({
    method: 'get',
    url: BASE_URL.concat(`/${clientId}/userProperties`),
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
  return response
}

async function getBookingReferenceValues(
  clientId: any,
  bookingReferenceId: any,
  query?: {
    limit?: number
    pageSize?: number
    offset?: number
    search?: string
  },
  options?: { signal?: AbortSignal },
) {
  const token = await getUserToken('clients')
  const response = await axios({
    method: 'get',
    url: BASE_URL.concat(
      `/${clientId}/bookingReferences/${bookingReferenceId}/values`,
    ),
    params: {
      ...(typeof query?.limit === 'number' ? { limit: query.limit } : {}),
      ...(typeof query?.pageSize === 'number'
        ? { pageSize: query.pageSize }
        : {}),
      ...(typeof query?.offset === 'number' && query.offset > 0
        ? { offset: query.offset }
        : {}),
      ...(query?.search ? { search: query.search } : {}),
    },
    headers: {
      Authorization: `Bearer ${token}`,
    },
    signal: options?.signal,
  })

  return response
}

async function createBookingReference(clientId: any, bookingReference: any) {
  clean(bookingReference)
  const token = await getUserToken('clients')
  const response = await axios({
    method: 'post',
    url: BASE_URL.concat(`/${clientId}/bookingReferences`),
    headers: {
      Authorization: `Bearer ${token}`,
    },
    data: {
      data: {
        ...bookingReference,
      },
    },
  })
  return response
}

async function updateBookingReference(
  clientId: any,
  bookingReferenceId: any,
  bookingReference: any,
) {
  clean(bookingReference)
  const token = await getUserToken('clients')
  const response = await axios({
    method: 'patch',
    url: BASE_URL.concat(
      `/${clientId}/bookingReferences/${bookingReferenceId}`,
    ),
    headers: {
      Authorization: `Bearer ${token}`,
    },
    data: {
      data: {
        ...bookingReference,
      },
    },
  })
  return response
}

async function deleteBookingReference(clientId: any, bookingReferenceId: any) {
  const token = await getUserToken('clients')
  const response = await axios({
    method: 'delete',
    url: BASE_URL.concat(
      `/${clientId}/bookingReferences/${bookingReferenceId}`,
    ),
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
  return response
}

const getClientFixedRoutes = async (
  id: number,
): Promise<AxiosResponse<FixedRoutes>> => {
  console.log(`fetching Client fixed routes ${id}`)
  const token = await getUserToken('clients')

  const response = await axios({
    method: 'get',
    url: BASE_URL?.concat(`/${id}/fixedRoutes`),
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
  return response
}

const deleteClientFixedRoute = async (
  clientId: number,
  fixedRouteId: number,
) => {
  const token = await getUserToken('clients')

  const response = await axios({
    method: 'delete',
    url: BASE_URL?.concat(`/${clientId}/fixedRoutes/${fixedRouteId}`),
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
  return response
}

const createClientFixedRoute = async (
  clientId: number,
  fixedRoute: FixedRoute,
) => {
  clean(fixedRoute)
  const token = await getUserToken('clients')

  const response = await axios({
    method: 'post',
    url: BASE_URL?.concat(`/${clientId}/fixedRoutes`),
    headers: {
      Authorization: `Bearer ${token}`,
    },
    data: {
      data: {
        ...fixedRoute,
      },
    },
  })
  return response
}

const updateClientFixedRoute = async (
  clientId: number,
  fixedRouteId: number,
  fixedRoute: FixedRoute,
) => {
  clean(fixedRoute)
  const token = await getUserToken('clients')

  const response = await axios({
    method: 'patch',
    url: BASE_URL?.concat(`/${clientId}/fixedRoutes/${fixedRouteId}`),
    headers: {
      Authorization: `Bearer ${token}`,
    },
    data: {
      data: {
        ...fixedRoute,
      },
    },
  })
  return response
}

export const getInvoiceSettings = async (clientId: any) => {
  const token = await getUserToken('clients')

  const response = await axios({
    method: 'GET',
    url: BASE_URL?.concat(`/${clientId}/invoice`),
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
  return response.data
}

export const createInvoiceSettings = async (
  clientId: any,
  data: InvoiceSettings,
) => {
  const token = await getUserToken('clients')

  const response = await axios({
    method: 'POST',
    url: BASE_URL?.concat(`/${clientId}/invoice`),
    headers: {
      Authorization: `Bearer ${token}`,
    },
    data: data,
  })
  return response.data
}

export const updateInvoiceSettings = async (
  clientId: any,
  data: InvoiceSettings,
) => {
  const token = await getUserToken('clients')

  const response = await axios({
    method: 'PATCH',
    url: BASE_URL?.concat(`/${clientId}/invoice`),
    headers: {
      Authorization: `Bearer ${token}`,
    },
    data: data,
  })
  return response.data
}

export const getClientTravelPolicies = async (clientId: any) => {
  const token = await getUserToken('clients')

  const response = await axios({
    method: 'GET',
    url: BASE_URL?.concat(`/${clientId}/travelPolicies`),
    headers: {
      Authorization: `Bearer ${token}`,
    },
  })
  return response.data
}

export const clientsAPI = {
  getUserProfiles,
  updateClientUserProfile,
  getClientsByParentId,
  getAllClientsByCurrentId,
  getAllDescendants,
  getDescendantsByClientId,
  getClient,
  getClientById,
  getClients,
  getClientsByPlatformId,
  createClient,
  updateClient,
  deleteClient,
  searchClients,
  getClientTravelPolicies,
  getBookingReferences,
  getBookingReferenceValues,
  getUserProperties,
  createBookingReference,
  updateBookingReference,
  deleteBookingReference,
  getClientFixedRoutes,
  createClientFixedRoute,
  updateClientFixedRoute,
  deleteClientFixedRoute,
  getInvoiceSettings,
  createInvoiceSettings,
  updateInvoiceSettings,
  // deleteInvoiceSettings,
}
